
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { TestTube, MapPin, RefreshCw } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

interface TestResult {
  success: boolean;
  rental_id?: string;
  tracking_point_id?: string;
  mileage_before?: number;
  mileage_after?: number;
  mileage_added?: number;
  test_coordinates?: { lat: number; lon: number };
  error?: string;
}

export const MileageSystemTester: React.FC = () => {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [courierId, setCourierId] = useState('');
  const [latitude, setLatitude] = useState('55.7558');
  const [longitude, setLongitude] = useState('37.6176');

  const testMileageCalculation = async () => {
    if (!courierId) {
      toast.error('Введите ID курьера для тестирования');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      console.log('Тестируем расчет пробега для курьера:', courierId);
      
      const response = await fetch('/api/test-mileage-calculation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          test_courier_id: courierId,
          test_lat: parseFloat(latitude),
          test_lon: parseFloat(longitude)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json() as TestResult;
      setTestResult(data);
      
      if (data.success) {
        toast.success('Тест выполнен успешно');
      } else {
        toast.error(data.error || 'Тест не удался');
      }
      
    } catch (err: any) {
      console.error('Ошибка при тестировании:', err);
      setTestResult({
        success: false,
        error: err.message || 'Неизвестная ошибка'
      });
      toast.error('Не удалось выполнить тест');
    } finally {
      setTesting(false);
    }
  };

  const generateRandomCoordinates = () => {
    // Генерируем случайные координаты в районе Москвы
    const baseLat = 55.7558;
    const baseLon = 37.6176;
    const randomLat = baseLat + (Math.random() - 0.5) * 0.02; // ~1 км радиус
    const randomLon = baseLon + (Math.random() - 0.5) * 0.02;
    
    setLatitude(randomLat.toFixed(6));
    setLongitude(randomLon.toFixed(6));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TestTube className="h-5 w-5 text-blue-500" />
          Тестирование системы пробега
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div>
            <Label htmlFor="courier-id">ID курьера</Label>
            <Input
              id="courier-id"
              value={courierId}
              onChange={(e) => setCourierId(e.target.value)}
              placeholder="Введите UUID курьера"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="latitude">Широта</Label>
              <Input
                id="latitude"
                type="number"
                step="0.000001"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="longitude">Долгота</Label>
              <Input
                id="longitude"
                type="number"
                step="0.000001"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={generateRandomCoordinates}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            <MapPin className="h-4 w-4" />
            Случайные координаты
          </Button>
        </div>

        <Button
          onClick={testMileageCalculation}
          disabled={testing || !courierId}
          className="w-full"
        >
          {testing ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Тестирование...
            </>
          ) : (
            <>
              <TestTube className="h-4 w-4 mr-2" />
              Запустить тест
            </>
          )}
        </Button>

        {testResult && (
          <div className={`p-4 rounded-lg border ${
            testResult.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant={testResult.success ? 'default' : 'destructive'}>
                  {testResult.success ? 'Успешно' : 'Ошибка'}
                </Badge>
              </div>
              
              {testResult.success ? (
                <div className="space-y-2 text-sm">
                  <div><strong>ID аренды:</strong> {testResult.rental_id}</div>
                  <div><strong>Пробег до:</strong> {testResult.mileage_before?.toFixed(3)} км</div>
                  <div><strong>Пробег после:</strong> {testResult.mileage_after?.toFixed(3)} км</div>
                  <div className="text-green-600">
                    <strong>Добавлено:</strong> {testResult.mileage_added?.toFixed(3)} км
                  </div>
                  <div className="text-xs text-gray-500">
                    Координаты: {testResult.test_coordinates?.lat}, {testResult.test_coordinates?.lon}
                  </div>
                </div>
              ) : (
                <div className="text-red-600 text-sm">
                  {testResult.error}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          Тест добавляет новую GPS точку для указанного курьера и проверяет, 
          что пробег аренды автоматически обновляется.
        </div>
      </CardContent>
    </Card>
  );
};
